import { useState } from 'react';

/**
 * Custom hook for number inputs that replaces value on first keystroke
 * Tracks "pristine" state - when focused but not yet modified
 *
 * @returns {Object} - Hook handlers and state
 * @returns {boolean} isPristine - Whether input is in pristine state (focused, not modified)
 * @returns {Function} handleFocus - Focus event handler
 * @returns {Function} handleKeyDown - KeyDown event handler that intercepts first digit
 * @returns {Function} handleBlur - Blur event handler
 *
 * @example
 * const { isPristine, handleFocus, handleKeyDown, handleBlur } = useReplaceOnFirstType();
 *
 * <input
 *   value={value}
 *   onChange={onChange}
 *   onFocus={handleFocus}
 *   onKeyDown={(e) => handleKeyDown(e, value, (newValue) => onChange({ target: { value: newValue } }))}
 *   onBlur={handleBlur}
 *   className={isPristine ? 'input-pristine' : ''}
 * />
 */
export const useReplaceOnFirstType = () => {
  const [isPristine, setIsPristine] = useState(false);

  const handleFocus = () => {
    setIsPristine(true);
  };

  const handleKeyDown = (e, currentValue, setValue) => {
    // Check if input is pristine and user pressed a digit or decimal point
    if (isPristine && /^[0-9.]$/.test(e.key)) {
      e.preventDefault();
      setValue(e.key);
      setIsPristine(false);
    } else if (isPristine && e.key !== 'Tab') {
      // Any other key (except Tab) exits pristine mode
      setIsPristine(false);
    }
  };

  const handleBlur = () => {
    setIsPristine(false);
  };

  return {
    isPristine,
    handleFocus,
    handleKeyDown,
    handleBlur,
  };
};
